/**
 * The DOM shell: header, galaxy strip (Planets tab only), panel, bottom sheet,
 * nav, toasts and modal, plus the action dispatcher every button routes
 * through (data-act + data-* params).
 */

import { PLANETS, type PlanetStat } from '../content/planets';
import { resource } from '../content/resources';
import { driftDef } from '../content/drifts';
import { PROJECTS } from '../content/projects';
import { ADDITIONS } from '../content/flags';
import { ROOMS } from '../content/rooms';
import { prospectorDef, PROSPECTOR_RARITY_COLOR } from '../content/prospectors';
import * as act from '../sim/actions';
import { fleetIncome, unlockCost } from '../sim/economy';
import { beginFold, canFold, completeFold } from '../sim/fold';
import { deliverContract, discardContract } from '../sim/market';
import { simulateOffline, type OfflineSummary } from '../sim/offline';
import { assignProspector, colonize, recruit } from '../sim/planets';
import { buyProject, canBuyProject } from '../sim/projects';
import { collectAsteroid } from '../sim/asteroids';
import { launchRover } from '../sim/rovers';
import { buyShip, grantShip } from '../sim/ships';
import { SHIPS, shipDef } from '../content/ships';
import { canStartChallenge, finishChallenge, startChallenge, viewGalaxy } from '../sim/challenges';
import { isMuted, play, toggleMute } from './sound';
import { nextRoomCost, upgradeRoom } from '../sim/rooms';
import { fleetStats } from '../sim/stats';
import type { SimEvent } from '../sim/events';
import type { GameState } from '../sim/state';
import { cur, esc, fmtFine, fmtNum, fmtTime, price } from './format';
import { morph } from './morph';
import { icon } from './icons';
import { bindMatrixGestures, clampSpan, renderNodeSheet } from './matrixMap';
import { newUiState, type BuyMode, type HoldView, type StationTab, type Tab, type View } from './view';
import { renderAssignSheet, renderPlanets } from './panels/planets';
import { renderProduction, renderRecipeSheet } from './panels/production';
import { renderMarket } from './panels/market';
import { renderProjects, renderShipSheet } from './panels/projects';
import { renderStation, renderStationHead, stationTabs } from './panels/station';
import { canBuyShip } from '../sim/ships';
import { hasProject } from '../sim/projects';
import { MATRIX_NODES } from '../content/matrix';
import { renderFold } from './panels/fold';

export interface AppHooks {
  getState(): GameState;
  save(): void;
  wipe(): void;
  /** The galaxy strip was shown or hidden; the canvas needs to re-measure. */
  galaxyShown(): void;
}

const TABS: readonly { id: Tab; label: string; icon: string }[] = [
  { id: 'planets', label: 'Planets', icon: icon('planet') },
  { id: 'production', label: 'Forge', icon: icon('factory') },
  { id: 'market', label: 'Market', icon: icon('tag') },
  { id: 'projects', label: 'Projects', icon: icon('research') },
  { id: 'fold', label: 'Fold', icon: icon('spiral') },
];

const RENDERERS: Readonly<Record<Tab, (v: View) => string>> = {
  planets: renderPlanets, production: renderProduction, market: renderMarket, projects: renderProjects, fold: renderFold,
};

export class AppShell {
  readonly ui = newUiState();
  readonly galaxyHost: HTMLElement;
  /** The galaxy camera, once the Pixi view exists. */
  galaxy: { zoomBy(f: number): void; focusPlanet(i: number): void; resetCamera(): void; readonly zoomed: boolean } | null = null;
  private readonly frame: HTMLElement;
  private readonly head: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly sheetBody: HTMLElement;
  private readonly nav: HTMLElement;
  private readonly toasts: HTMLElement;
  private readonly modal: HTMLElement;

  constructor(root: HTMLElement, private readonly hooks: AppHooks) {
    root.innerHTML = `
      <div class="vm-frame" data-tab="planets">
        <header class="vm-head"></header>
        <div class="vm-galaxy"><div class="vm-gal-tools">
          <button class="vm-btn ghost" data-act="galZoom" data-dir="in" aria-label="Zoom in">+</button>
          <button class="vm-btn ghost" data-act="galZoom" data-dir="out" aria-label="Zoom out">−</button>
          <button class="vm-btn ghost" data-act="galZoom" data-dir="home" aria-label="Whole galaxy">⌖</button>
        </div></div>
        <div class="vm-panel"></div>
        <div class="vm-sheet" hidden><div class="vm-sheet-scrim" data-act="closeSheet"></div><div class="vm-sheet-body"></div></div>
        <nav class="vm-nav"></nav>
        <div class="vm-toasts"></div>
      </div>
      <div class="vm-modal" hidden></div>`;
    this.frame = root.querySelector('.vm-frame')!;
    this.head = root.querySelector('.vm-head')!;
    this.galaxyHost = root.querySelector('.vm-galaxy')!;
    this.panel = root.querySelector('.vm-panel')!;
    this.sheet = root.querySelector('.vm-sheet')!;
    this.sheetBody = root.querySelector('.vm-sheet-body')!;
    this.nav = root.querySelector('.vm-nav')!;
    this.toasts = root.querySelector('.vm-toasts')!;
    this.modal = root.querySelector('.vm-modal')!;
    root.addEventListener('click', (e) => this.onClick(e));
    bindMatrixGestures(this.panel, this.ui, (id) => { this.ui.sheet = { kind: 'node', id }; this.refresh(); });
  }

  private get state(): GameState {
    return this.hooks.getState();
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  refresh(): void {
    const state = this.state;
    const view: View = { state, stats: fleetStats(state), ui: this.ui };
    const station = this.ui.screen === 'station';
    const frameTab = station ? 'station' : this.ui.tab;
    if (this.frame.dataset.tab !== frameTab) {
      this.frame.dataset.tab = frameTab;
      this.hooks.galaxyShown();
    }
    morph(this.head, this.renderHead(view));
    morph(this.nav, station ? this.renderStationNav(view) : this.renderNav(view));
    morph(this.panel, station ? renderStation(view) : RENDERERS[this.ui.tab](view));
    const sheet = this.renderSheet(view);
    this.sheet.hidden = sheet === '';
    // A Matrix node sheet is non-modal: the map stays live above it, so the next node is one tap away.
    this.sheet.classList.toggle('peek', this.ui.sheet?.kind === 'node');
    morph(this.sheetBody, sheet);
  }

  private renderSheet(v: View): string {
    const s = this.ui.sheet;
    if (!s) return '';
    if (s.kind === 'recipe') return renderRecipeSheet(v, s.workshop, s.slot);
    if (s.kind === 'assign') return renderAssignSheet(v, s.planet);
    if (s.kind === 'ship') return renderShipSheet(v, s.id);
    return renderNodeSheet(v, s.id);
  }

  private renderHead(v: View): string {
    const { state, stats } = v;
    const drift = ADDITIONS.drifts ? driftDef(state.run.drift) : null;
    // Dev-server fast-forward (never in a build). Labelled DEV and lit when on, so it
    // isn't mistaken for a game setting: the real game always runs in real time, as IPM does.
    const dev = import.meta.env.DEV
      ? `<button class="vm-dev${this.ui.devSpeed > 1 ? ' on' : ''}" data-act="devSpeed" title="Dev fast-forward (dev server only)">DEV ×${this.ui.devSpeed}</button>`
      : '';
    const mute = `<button class="vm-mute" data-act="mute" aria-label="${isMuted() ? 'Unmute' : 'Mute'}">${icon(isMuted() ? 'muted' : 'speaker')}</button>`;
    const chip = (c: 'te' | 'sc' | 'vs', n: number) => `<span class="vm-chip ${c}">${cur(c)} ${fmtNum(n)}</span>`;
    if (this.ui.screen === 'station') return `${dev}${renderStationHead(v)}`;
    return `${dev}${mute}
      <div class="vm-title-row">
        <h1 class="vm-title">VoidMiner</h1>
        ${drift ? `<span class="vm-sub"><span style="color:${drift.color}">${esc(drift.name)}</span></span>` : ''}
      </div>
      <div class="vm-gc"><span class="cur gc">◈</span> <b>${fmtNum(state.run.gc)}</b> <span class="vm-rate">+${fmtFine(fleetIncome(state, stats))}/s</span></div>
      <div class="vm-chips">${this.renderChips(v, chip)}${this.renderStationButton(v)}</div>
      ${this.renderSwitcher(v)}`;
  }

  /** A currency chip appears once that currency has meant something, so a new player sees GC alone. */
  private renderChips(v: View, chip: (c: 'te' | 'sc' | 'vs', n: number) => string): string {
    const { state } = v;
    const show = {
      te: state.te > 0 || state.folds > 0,
      sc: state.sc > 0 || state.folds > 0 || Object.keys(state.prospectors).length > 0,
      vs: state.vs > 0 || state.challengesDone > 0 || state.challenge !== null,
    };
    return (['te', 'sc', 'vs'] as const).filter((c) => show[c]).map((c) => chip(c, state[c])).join('');
  }

  /** Station is open once there is something to keep: a Fold done, or crew to hire after Management. */
  private stationOpen(state: GameState): boolean {
    return state.folds > 0 || hasProject(state, 'management') || state.ships.length > 0;
  }

  /** Something in the Station can be bought or started right now. */
  private stationReady(state: GameState): Partial<Record<StationTab, boolean>> {
    return {
      rooms: ROOMS.some((r) => { const c = nextRoomCost(state, r.id); return c !== null && state.te >= c; }),
      ships: SHIPS.some((s) => canBuyShip(state, s.id)),
      matrix: MATRIX_NODES.some((n) => act.canReach(state, n.id) && (act.nextLevelCost(state, n.id) ?? Infinity) <= state.vs),
      challenge: canStartChallenge(state),
    };
  }

  private renderStationButton(v: View): string {
    if (!this.stationOpen(v.state)) return '';
    const station = this.ui.screen === 'station';
    const pip = !station && Object.values(this.stationReady(v.state)).some(Boolean) ? '<span class="pip"></span>' : '';
    return `<button class="vm-chip vm-station-btn${station ? ' on' : ''}" data-act="${station ? 'launch' : 'station'}">${station ? '◂ Galaxy' : '⬡ Station'}${pip}</button>`;
  }

  private renderStationNav(v: View): string {
    const ready = this.stationReady(v.state);
    return stationTabs().map((t) => `<button class="${this.ui.stationTab === t.id ? 'active' : ''}" data-act="stationTab" data-tab="${t.id}">
      <span class="vm-nav-icon">${t.icon}</span><span>${t.label}</span>${ready[t.id] ? '<span class="pip"></span>' : ''}</button>`).join('');
  }

  /** Main / Challenge switcher, shown only while a Challenge runs. */
  private renderSwitcher(v: View): string {
    const c = v.state.challenge;
    if (!c) return '';
    const left = fmtTime(Math.max(0, c.endsAt - v.state.clock));
    const tab = (to: 'main' | 'challenge', label: string) =>
      `<button class="vm-btn ${c.viewing === to ? 'on' : 'ghost'}" data-act="viewGalaxy" data-to="${to}">${label}</button>`;
    return `<div class="vm-switch">${tab('main', 'Main galaxy')}${tab('challenge', `Challenge · ${left}`)}</div>`;
  }

  private renderNav(v: View): string {
    const { state, stats } = v;
    const next = act.nextPlanetIndex(state);
    const pips: Partial<Record<Tab, boolean>> = {
      planets: next >= 0 && state.run.gc >= unlockCost(state, stats, next),
      production: [...state.run.smelters, ...state.run.crafters].some((s) => !s.recipe) && state.run.planets.filter((p) => p.owned).length >= 2,
      market: ADDITIONS.contracts && state.contracts.some((c) => c && (state.run.hold[c.item] ?? 0) >= c.qty),
      projects: PROJECTS.some((p) => canBuyProject(state, p.id)),
      fold: !!state.pendingFold || canFold(state),
    };
    return TABS.map((t) => `<button class="${this.ui.tab === t.id ? 'active' : ''}" data-act="tab" data-tab="${t.id}">
      <span class="vm-nav-icon">${t.icon}</span><span>${t.label}</span>${pips[t.id] ? '<span class="pip"></span>' : ''}</button>`).join('');
  }

  // ── Feedback ─────────────────────────────────────────────────────────────

  /** A new web build has downloaded: a small bar offering the reload (never forced mid-session). */
  offerUpdate(apply: () => void): void {
    if (this.frame.querySelector('.vm-update')) return;
    const bar = document.createElement('div');
    bar.className = 'vm-update';
    bar.innerHTML = '<span>A new version is ready.</span><button class="vm-btn">Reload</button>';
    bar.querySelector('button')!.addEventListener('click', () => { this.hooks.save(); apply(); });
    this.frame.appendChild(bar);
  }

  toast(text: string, kind: 'good' | 'bad' | 'info' = 'info'): void {
    const el = document.createElement('div');
    el.className = `vm-toast ${kind}`;
    el.innerHTML = text;
    this.toasts.appendChild(el);
    while (this.toasts.children.length > 3) this.toasts.firstElementChild?.remove();
    setTimeout(() => el.remove(), 3200);
  }

  onSimEvent(e: SimEvent): void {
    if (e.kind === 'ship') {
      play('unlock');
      this.toast(`New ship earned: <b>${esc(shipDef(e.id).name)}</b> joins the fleet`, 'good');
      return;
    }
    if (e.kind === 'challenge') {
      play('fold');
      this.toast(`Challenge over at ${price('gc', e.value)} · +${e.vs} ${cur('vs')} for the Matrix`, 'good');
      return;
    }
    if (e.kind === 'rover') {
      const found = e.prospector ? prospectorDef(e.prospector) : null;
      play('chime');
      this.toast(`Rover home: +${e.sc} ${cur('sc')}${found ? ` · found <span style="color:${PROSPECTOR_RARITY_COLOR[found.rarity]}">${esc(found.name)}</span>` : ''}`, 'good');
      return;
    }
    const def = resource(e.id);
    if (def.kind === 'ore' && def.tier === 0) return;
    const glyph = def.kind === 'item' ? `<span style="color:${def.color}">${def.glyph}</span> ` : '';
    const sc = e.sc ? ` · first craft +${e.sc} ${cur('sc')}` : '';
    const kind = { ore: 'ore', alloy: 'bar', item: 'item' }[def.kind];
    this.toast(`New ${kind}: ${glyph}${esc(def.name)}${sc}`, e.sc ? 'good' : 'info');
  }

  showOffline(summary: OfflineSummary): void {
    const lines = [
      `<dt>Away for</dt><dd>${fmtTime(summary.seconds)}</dd>`,
      `<dt>Value hauled and refined</dt><dd>${price('gc', summary.value)}</dd>`,
      summary.sc ? `<dt>Stellar Cores</dt><dd>${price('sc', summary.sc)}</dd>` : '',
      summary.rovers ? `<dt>Rover missions home</dt><dd>${summary.rovers}</dd>` : '',
      summary.vs ? `<dt>Void Shards</dt><dd>${price('vs', summary.vs)}</dd>` : '',
      summary.ships.length ? `<dt>Ships earned</dt><dd>${summary.ships.map((id) => esc(shipDef(id).name)).join(', ')}</dd>` : '',
    ].join('');
    this.modal.innerHTML = `<div class="vm-modal-box">
      <h3>While you were away</h3>
      <dl class="vm-kv" style="font-size:13px">${lines}</dl>
      ${summary.capped ? `<p class="vm-note" style="margin-top:10px">Your fleet stops after ${fmtFine(summary.capHours)}h away. Build the <b>Archive</b> to keep it running longer.</p>` : ''}
      <div style="margin-top:12px"><button class="vm-btn wide" data-act="closeModal">Back to your galaxy</button></div>
    </div>`;
    this.modal.hidden = false;
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  tapAsteroid(): void {
    const got = collectAsteroid(this.state, (e) => this.onSimEvent(e));
    if (got) play('pop');
    if (got) this.toast(`Asteroid mined: +${fmtNum(got.amount)} ${esc(resource(got.ore).name)}`, 'good');
    this.refresh();
  }

  /**
   * One step back (the Android back button): close the offline card or a
   * sheet, leave the Station, return to Planets. False when there is nowhere
   * further back to go, so the app can go to the background.
   */
  back(): boolean {
    const ui = this.ui;
    if (!this.modal.hidden) this.run('closeModal', {});
    else if (ui.sheet) ui.sheet = null;
    else if (ui.screen === 'station') this.run('launch', {});
    else if (ui.tab !== 'planets') this.run('tab', { tab: 'planets' });
    else return false;
    this.refresh();
    return true;
  }

  tapPlanet(index: number): void {
    this.ui.selected = index;
    this.ui.screen = 'galaxy';
    this.ui.tab = 'planets';
    this.refresh();
  }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!el || (el as HTMLButtonElement).disabled) return;
    this.run(el.dataset.act!, el.dataset);
    this.refresh();
  }

  private run(name: string, d: DOMStringMap): void {
    const state = this.state;
    const ui = this.ui;
    switch (name) {
      case 'tab': ui.tab = d.tab as Tab; ui.screen = 'galaxy'; ui.sheet = null; this.panel.scrollTop = 0; break;
      case 'station': ui.screen = 'station'; ui.stationTab = (d.tab as StationTab) || 'hub'; ui.sheet = null; this.panel.scrollTop = 0; break;
      case 'stationTab': ui.stationTab = d.tab as StationTab; ui.sheet = null; this.panel.scrollTop = 0; break;
      case 'launch':
        ui.screen = 'galaxy'; ui.sheet = null; this.panel.scrollTop = 0;
        if (ui.foldReport) {
          ui.foldReport = null; ui.tab = 'planets'; ui.selected = 0; play('unlock');
          this.toast(`A new galaxy. Buy ${PLANETS[0]!.name} to start mining.`, 'good');
        }
        break;
      case 'select': ui.selected = Number(d.planet); ui.tab = 'planets'; ui.screen = 'galaxy'; break;
      case 'buyMode': ui.buyMode = (d.mode === 'max' ? 'max' : Number(d.mode)) as BuyMode; break;
      case 'upgrade': if (act.upgradePlanet(state, Number(d.planet), d.stat as PlanetStat, d.count === 'max' ? 'max' : Number(d.count))) play('buy'); break;
      case 'buyPlanet':
        if (act.buyPlanet(state, Number(d.planet))) {
          play('unlock');
          ui.selected = Number(d.planet);
          this.toast(`${PLANETS[Number(d.planet)]!.name} unlocked`, 'good');
          this.hooks.save();
        }
        break;
      case 'rover':
        if (launchRover(state, Number(d.planet), Number(d.mission))) { play('pop'); this.toast(`Rover sent to ${PLANETS[Number(d.planet)]!.name}`, 'info'); this.hooks.save(); }
        break;
      case 'colonize': if (colonize(state, Number(d.planet))) { play('unlock'); this.toast(`${PLANETS[Number(d.planet)]!.name} colony grows`, 'good'); } break;
      case 'openAssign': ui.sheet = { kind: 'assign', planet: Number(d.planet) }; break;
      case 'assign': assignProspector(state, Number(d.planet), d.id || null); ui.sheet = null; break;
      case 'openRecipe': ui.sheet = { kind: 'recipe', workshop: d.workshop as act.Workshop, slot: Number(d.slot) }; break;
      case 'setRecipe': act.setRecipe(state, d.workshop as act.Workshop, Number(d.slot), d.recipe || null); ui.sheet = null; break;
      case 'closeSheet': ui.sheet = null; break;
      case 'openShip': ui.sheet = { kind: 'ship', id: d.id! }; break;
      case 'project':
        if (buyProject(state, d.id!)) {
          play('unlock');
          this.toast(`Project complete: ${esc(PROJECTS.find((p) => p.id === d.id)!.name)}`, 'good');
          this.hooks.save();
        }
        break;
      case 'openProjects': ui.tab = 'projects'; ui.screen = 'galaxy'; ui.sheet = null; this.panel.scrollTop = 0; break;
      case 'buyRecipe': if (act.buyRecipe(state, d.id!)) { play('unlock'); this.toast(`Recipe unlocked: ${esc(resource(d.id!).name)}`, 'good'); } break;
      case 'buySlot': if (act.buySlot(state, d.workshop as act.Workshop)) play('buy'); break;
      case 'sell': if (act.sell(state, d.id!, d.qty === 'all' ? Infinity : Number(d.qty))) play('coin'); break;
      case 'sellKind': if (act.sellKind(state, d.kind as 'ore' | 'alloy')) play('coin'); break;
      case 'mute': toggleMute(); break;
      case 'startChallenge':
        if (startChallenge(state)) { play('unlock'); this.toast('Challenge started: a fresh galaxy for 24 hours', 'good'); this.hooks.save(); }
        break;
      case 'viewGalaxy':
        if (viewGalaxy(state, d.to as 'main' | 'challenge')) { ui.selected = 0; ui.sheet = null; ui.tab = 'planets'; ui.screen = 'galaxy'; this.panel.scrollTop = 0; }
        break;
      case 'endChallenge': finishChallenge(state, (e) => this.onSimEvent(e)); this.hooks.save(); break;
      case 'openRooms': ui.screen = 'station'; ui.stationTab = 'rooms'; ui.sheet = null; this.panel.scrollTop = 0; break;
      case 'holdView': ui.holdView = d.view as HoldView; break;
      case 'deliverContract': if (deliverContract(state, Number(d.slot))) this.toast(`Contract delivered`, 'good'); break;
      case 'discardContract': discardContract(state, Number(d.slot)); break;
      case 'fit': if (act.feedFitting(state, d.id!, Infinity)) this.toast(`Fitted ${esc(resource(d.id!).name)}`, 'good'); break;
      case 'socket': if (!act.unsocketFitting(state, d.id!)) act.socketFitting(state, d.id!); break;
      case 'buySocket': act.buySocket(state); break;
      case 'room': if (upgradeRoom(state, d.id!)) { play('unlock'); this.hooks.save(); } break;
      case 'buyShip': if (buyShip(state, d.id!)) { play('unlock'); this.toast(`${esc(shipDef(d.id!).name)} joins the fleet`, 'good'); this.hooks.save(); } break;
      case 'recruit': {
        const id = recruit(state);
        if (id) play('chime');
        if (id) {
          const def = prospectorDef(id);
          this.toast(`<span style="color:${PROSPECTOR_RARITY_COLOR[def.rarity]}">${esc(def.name)}</span> ${state.prospectors[id]! > 1 ? `levels up to ${state.prospectors[id]}` : 'joins the crew'}`, 'good');
          this.hooks.save();
        }
        break;
      }
      case 'allocateNode': act.allocateNode(state, d.id!); break;
      case 'refundNode': act.refundNode(state, d.id!); break;
      case 'galZoom': {
        const g = this.galaxy;
        if (!g) break;
        // + flies in to the selected planet first, then keeps zooming there.
        if (d.dir === 'in') { if (g.zoomed) g.zoomBy(1.5); else g.focusPlanet(ui.selected); }
        else if (d.dir === 'out') g.zoomBy(1 / 1.5);
        else g.resetCamera();
        break;
      }
      case 'mxZoom':
        if (d.dir === 'home') ui.camera = { x: 0, y: 0, span: 760 };
        else ui.camera.span = clampSpan(ui.camera.span * (d.dir === 'in' ? 0.75 : 1.33));
        break;
      case 'beginFold':
        if (beginFold(state)) { ui.foldDrift = null; this.hooks.save(); this.panel.scrollTop = 0; }
        break;
      case 'pickDrift': ui.foldDrift = d.id!; break;
      case 'completeFold': {
        // Dock at the Station with a report of what this galaxy sold.
        const offer = state.pendingFold;
        let depth = 0;
        state.run.planets.forEach((p, i) => { if (p.owned) depth = i + 1; });
        const report = offer && { value: state.run.galaxyValue, depth, te: offer.te + offer.distilled, sc: offer.sc };
        if (report && completeFold(state, ui.foldDrift)) {
          play('fold');
          ui.selected = 0;
          ui.tab = 'planets';
          ui.foldDrift = null;
          ui.foldReport = report;
          ui.screen = 'station';
          ui.stationTab = 'hub';
          ui.sheet = null;
          this.panel.scrollTop = 0;
          this.hooks.save();
        }
        break;
      }
      case 'closeModal': this.modal.hidden = true; this.modal.innerHTML = ''; break;
      case 'devSpeed': ui.devSpeed = ui.devSpeed === 1 ? 10 : ui.devSpeed === 10 ? 100 : 1; break;
      case 'dev': this.dev(d.what!); break;
    }
  }

  private dev(what: string): void {
    if (!import.meta.env.DEV) return;
    const state = this.state;
    const grant = (gc: number) => { state.run.gc += gc; state.run.lifetimeGc += gc; state.lifetime.gc += gc; };
    switch (what) {
      case 'gc': grant(1e6); break;
      case 'gcx': grant(Math.max(1e6, state.run.gc * 999)); break;
      case 'value': state.run.galaxyValue += 10e6; break;
      case 'ores': for (const ore of ['ferrite', 'cuprite', 'silica', 'cobaltite', 'lumen']) state.run.hold[ore] = (state.run.hold[ore] ?? 0) + 20000; break;
      case 'te': state.te += 100; break;
      case 'folds': state.folds += 2; break;
      case 'challengeEnd': if (state.challenge) state.challenge.endsAt = state.clock; break;
      case 'sc': state.sc += 100; break;
      case 'storeShips': for (const s of SHIPS) if (s.source.kind === 'store') grantShip(state, s.id); break;
      case 'vs': state.vs += 100; break;
      case 'offline': this.showOffline(simulateOffline(state, 3600)); break;
      case 'wipe': this.hooks.wipe(); break;
    }
  }
}

